import {
  ACESFilmicToneMapping,
  CanvasTexture,
  EquirectangularReflectionMapping,
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshPhysicalMaterial,
  PMREMGenerator,
  PerspectiveCamera,
  RepeatWrapping,
  SRGBColorSpace,
  Scene,
  Vector2,
  WebGLRenderer,
} from "three";
import { SVGLoader } from "three/addons/loaders/SVGLoader.js";

/**
 * The pixel genies as chrome: each flat mark is pushed out into a slab with a
 * rounded edge and given a painted panorama to mirror. One slab is shown at a time. The next one is swapped in
 * while the slab is edge-on, so a half turn always ends on a new genie.
 */

interface ChromeOptions {
  /** The marks, in the order they appear. */
  marks: string[];
  /** The turn in half turns, read every frame: 0 is the first mark face-on, 1 the second. */
  turn: () => number;
  /** Told when the mark facing the viewer changes, and on every frame what angle it stands at. */
  onFace?: (n: number) => void;
  onAngle?: (degrees: number) => void;
}

/** Height of every genie in scene units; the camera is set to this. */
const TALL = 2;
/** How far a slab is pushed out, as a share of the mark's own height. */
const DEPTH = 0.1;

/**
 * What the chrome mirrors, painted as one panorama: a bright sky that ends in a
 * hard black horizon, the classic chrome reading, with thin bars of glare across
 * it and the page's own colours low down. A flat face only mirrors a narrow cone
 * of it, so the contrast is packed close around the horizon and the bars are
 * thin enough that a small tilt drags them across the face.
 */
export function panorama(): CanvasTexture {
  const W = 2048;
  const H = 1024;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, "#3d434b");
  sky.addColorStop(0.3, "#8d959e");
  sky.addColorStop(0.44, "#d4dbe1");
  sky.addColorStop(0.492, "#ffffff");
  sky.addColorStop(0.508, "#14161a");
  sky.addColorStop(0.55, "#4a4e53");
  sky.addColorStop(0.66, "#c9ced2");
  sky.addColorStop(0.8, "#6a6e73");
  sky.addColorStop(1, "#2a2c2f");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);

  // tall bars of white, drawn twice so the seam of the panorama is covered
  const bar = (u: number, width: number, top: number, bottom: number, color: string) => {
    for (const shift of [-W, 0, W]) {
      const x = u * W + shift;
      const glow = ctx.createLinearGradient(x - width, 0, x + width, 0);
      glow.addColorStop(0, "rgba(0,0,0,0)");
      glow.addColorStop(0.5, color);
      glow.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(x - width, top * H, width * 2, (bottom - top) * H);
    }
  };
  for (let n = 0; n < 16; n++) {
    const u = n / 16 + 0.011;
    bar(u, n % 4 === 0 ? 34 : 9, 0.2, 0.497, "rgba(255,255,255,0.95)");
    bar(u + 0.031, 6, 0.503, 0.8, n % 2 ? "rgba(255,255,255,0.85)" : "rgba(215,255,0,0.9)");
  }
  bar(0.12, 46, 0.52, 0.7, "rgba(255,79,163,0.85)");
  bar(0.4, 46, 0.52, 0.7, "rgba(255,106,31,0.85)");
  bar(0.62, 46, 0.3, 0.48, "rgba(201,184,237,0.8)");
  bar(0.9, 46, 0.52, 0.7, "rgba(215,255,0,0.85)");

  const texture = new CanvasTexture(canvas);
  texture.mapping = EquirectangularReflectionMapping;
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

/**
 * A slow swell for the metal's surface, as a tiling normal map. A dead flat face
 * mirrors one patch of sky and reads as a white card; with the swell the horizon
 * and the bars bend across it, which is what makes it look like chrome.
 */
export function swell(): CanvasTexture {
  const N = 256;
  const canvas = document.createElement("canvas");
  canvas.width = N;
  canvas.height = N;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
  const image = ctx.createImageData(N, N);
  // whole numbers of waves across the tile, so it repeats without a seam
  const waves = [
    [1, 1, 1, 0.3],
    [2, -1, 0.55, 1.7],
    [1, 3, 0.35, 4.1],
    [3, 2, 0.22, 2.6],
  ];
  const TAU = Math.PI * 2;
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      let dx = 0;
      let dy = 0;
      for (const [fx, fy, power, phase] of waves) {
        const slope = Math.cos(TAU * ((fx * x + fy * y) / N) + phase) * power;
        dx += slope * fx;
        dy += slope * fy;
      }
      const nx = -dx * 0.16;
      const ny = -dy * 0.16;
      const long = Math.hypot(nx, ny, 1);
      const i = (y * N + x) * 4;
      image.data[i] = (nx / long) * 127.5 + 127.5;
      image.data[i + 1] = (ny / long) * 127.5 + 127.5;
      image.data[i + 2] = (1 / long) * 127.5 + 127.5;
      image.data[i + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  const texture = new CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = RepeatWrapping;
  // the faces carry the mark's own units as uv, about a thousand to a genie
  texture.repeat.setScalar(1 / 900);
  return texture;
}

/** One mark as a slab, standing upright, centred, TALL high. */
function slab(svg: string, material: MeshPhysicalMaterial): Mesh {
  const shapes = new SVGLoader().parse(svg).paths.flatMap(path => SVGLoader.createShapes(path));
  const flat = new ExtrudeGeometry(shapes, { depth: 1, bevelEnabled: false });
  flat.computeBoundingBox();
  const high = flat.boundingBox ? flat.boundingBox.max.y - flat.boundingBox.min.y : 1000;
  flat.dispose();

  const bevel = high * 0.008;
  const geometry = new ExtrudeGeometry(shapes, {
    depth: high * DEPTH,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 4,
    curveSegments: 5,
  });
  // svg runs downwards; a half turn about x stands the mark up without mirroring it.
  geometry.rotateX(Math.PI);
  geometry.center();
  geometry.scale(TALL / high, TALL / high, TALL / high);
  return new Mesh(geometry, material);
}

export function chrome(canvas: HTMLCanvasElement, { marks, turn, onFace, onAngle }: ChromeOptions): () => void {
  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;

  const scene = new Scene();
  const pmrem = new PMREMGenerator(renderer);
  const sky = panorama();
  const env = pmrem.fromEquirectangular(sky);
  scene.environment = env.texture;

  const camera = new PerspectiveCamera(26, 1, 0.1, 50);
  camera.position.set(0, 0, 6.4);

  const waves = swell();
  const material = new MeshPhysicalMaterial({
    color: 0xffffff,
    metalness: 1,
    roughness: 0.09,
    envMapIntensity: 1.15,
    normalMap: waves,
    normalScale: new Vector2(0.4, 0.4),
  });
  const stand = new Group();
  scene.add(stand);

  let dead = false;
  let slabs: Mesh[] = [];
  void Promise.all(marks.map(src => fetch(src).then(res => res.text()))).then(files => {
    if (dead) return;
    slabs = files.map((svg, n) => {
      const mesh = slab(svg, material);
      // every other mark is met from behind, so it is set facing backwards to be read the right way round.
      mesh.rotation.y = n % 2 ? Math.PI : 0;
      mesh.visible = false;
      stand.add(mesh);
      return mesh;
    });
  });

  const size = () => {
    const { clientWidth: w, clientHeight: h } = canvas;
    if (!w || !h) return;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    // a narrow frame pulls the camera back so the widest genie still fits
    camera.position.z = 6.4 * Math.max(1, 0.72 / camera.aspect);
    camera.updateProjectionMatrix();
  };
  const sized = new ResizeObserver(size);
  sized.observe(canvas);
  size();

  // the pointer tips the slab a little, which is what makes the glare travel
  const tip = { x: 0, y: 0, tx: 0, ty: 0 };
  const point = (e: PointerEvent) => {
    tip.tx = (e.clientX / window.innerWidth - 0.5) * 0.5;
    tip.ty = (e.clientY / window.innerHeight - 0.5) * 0.3;
  };
  window.addEventListener("pointermove", point);

  let raf = 0;
  let seen = true;
  let at = turn();
  let face = -1;
  const draw = (now: number) => {
    raf = 0;
    if (!seen || document.hidden) return;
    raf = requestAnimationFrame(draw);
    if (!slabs.length) return;

    at += (turn() - at) * 0.12;
    tip.x += (tip.tx - tip.x) * 0.06;
    tip.y += (tip.ty - tip.y) * 0.06;

    const n = Math.min(slabs.length - 1, Math.max(0, Math.round(at)));
    if (n !== face) {
      face = n;
      slabs.forEach((mesh, k) => (mesh.visible = k === n));
      onFace?.(n);
    }
    const t = now / 1000;
    stand.rotation.y = at * Math.PI + tip.x + Math.sin(t * 0.6) * 0.05;
    stand.rotation.x = tip.y + Math.sin(t * 0.45) * 0.03;
    stand.position.y = Math.sin(t * 0.8) * 0.03;
    // the swell drifts, so the glare keeps moving while the genie stands still
    waves.offset.set(t * 0.012, t * 0.02);
    onAngle?.(Math.round(at * 180));
    renderer.render(scene, camera);
  };
  const wake = () => {
    if (!raf) raf = requestAnimationFrame(draw);
  };
  const view = new IntersectionObserver(([entry]) => {
    seen = entry.isIntersecting;
    if (seen) wake();
  });
  view.observe(canvas);
  document.addEventListener("visibilitychange", wake);
  wake();

  return () => {
    dead = true;
    cancelAnimationFrame(raf);
    view.disconnect();
    sized.disconnect();
    window.removeEventListener("pointermove", point);
    document.removeEventListener("visibilitychange", wake);
    slabs.forEach(mesh => mesh.geometry.dispose());
    sky.dispose();
    waves.dispose();
    material.dispose();
    env.dispose();
    pmrem.dispose();
    renderer.dispose();
  };
}
